import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import { getAllCountries } from '../../utils/countryTimezones';

const CountrySelector = ({ 
  label, 
  value = '', 
  onChange, 
  placeholder = 'Select a country',
  error,
  required = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const countries = getAllCountries();

  // Find selected country from value
  useEffect(() => {
    if (value) {
      const country = countries.find(c => 
        c.name === value || c.code === value
      );
      setSelectedCountry(country);
    } else {
      setSelectedCountry(null);
    }
  }, [value, countries]);

  // Filter countries based on search term
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    onChange(country.name);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-text-primary mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div
        className={`
          relative w-full px-3 py-2 border rounded-lg cursor-pointer
          transition-colors duration-200 bg-white
          ${error 
            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
            : 'border-border-primary focus:border-primary-500 focus:ring-primary-500'
          }
          ${isOpen ? 'ring-2 ring-opacity-20' : ''}
          hover:border-border-secondary
        `}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <span className={selectedCountry ? 'text-text-primary' : 'text-text-tertiary'}>
            {selectedCountry ? selectedCountry.name : placeholder}
          </span>
          <Icon 
            name={isOpen ? 'ChevronUp' : 'ChevronDown'} 
            size={20} 
            className="text-text-secondary"
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-border-primary rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-border-primary">
            <div className="relative">
              <Icon 
                name="Search" 
                size={16} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary"
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full pl-10 pr-3 py-2 border border-border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Country List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className={`
                    px-3 py-2 cursor-pointer transition-colors duration-150
                    hover:bg-background-secondary
                    ${selectedCountry?.code === country.code ? 'bg-primary-50 text-primary-600' : 'text-text-primary'}
                  `}
                  onClick={() => handleCountrySelect(country)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{country.name}</span>
                    <span className="text-xs text-text-tertiary">{country.code}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-text-tertiary text-sm">
                No countries found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default CountrySelector;